import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Holidays from 'date-holidays';
import { formatTime } from './formatUtils';

interface TimeEntry {
  startTime: string;
  endTime?: string;
  breakMinutes: number;
  pausedDuration?: number;
  pauseStart?: string;
  type?: string;
}

interface WorkSchedule {
  dayOfWeek: string;
  startTime?: string;
  endTime?: string;
  hours?: number;
  mode?: 'fixed' | 'flex';
  validFrom?: string;
  validTo?: string;
}

interface Summary {
  plannedHours: number;
  totalHours: number;
  overtime: number;
  undertime: number;
}

export interface ReportDayRow {
  date: string;
  weekday: string;
  isHoliday: boolean;
  holidayName?: string | null;
  soll: number;
  ist: number;
  paidHolidayHours: number;
  workedHours: number;
  overtime: number;
  undertime: number;
  entries: TimeEntry[];
}

interface MonthlySummary extends Summary {
  month: string;
}

interface WeeklySummary extends Summary {
  week: string;
}

export interface ReportExportOptions {
  monthly: boolean;
  weekly: boolean;
  daily: boolean;
  monthlySummaries?: MonthlySummary[];
  weeklySummaries?: WeeklySummary[];
  dailyRows?: ReportDayRow[];
}

export interface UserReportExportData {
  username: string;
  entries: TimeEntry[];
  summary: Summary;
  monthlySummaries?: MonthlySummary[];
  weeklySummaries?: WeeklySummary[];
  schedule: WorkSchedule[];
  dailyRows?: ReportDayRow[];
}

const hd = new Holidays('DE', 'BE');

function loadLogoAsBase64(): Promise<string> {
  return fetch('/logo.jpeg')
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === 'string') resolve(reader.result);
            else reject('Fehler beim Laden des Logos');
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
}

function parseTimeString(time?: string): number {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

function getWeekdayNumber(dateStr: string): number {
  return new Date(dateStr).getDay();
}

function weekdayNameToNumber(name?: string): number | null {
  if (!name) return null;

  const map: Record<string, number> = {
    sonntag: 0,
    montag: 1,
    dienstag: 2,
    mittwoch: 3,
    donnerstag: 4,
    freitag: 5,
    samstag: 6,
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  return map[name.toLowerCase()] ?? null;
}

function getScheduleForDate(schedule: WorkSchedule[], date: Date, weekdayNumber: number): WorkSchedule | null {
  const dateTime = date.getTime();
  const matchingDay = schedule.filter((s) => weekdayNameToNumber(s.dayOfWeek) === weekdayNumber);

  const historical = matchingDay
    .filter((s) => {
      if (!s.validFrom) return false;
      const validFrom = new Date(s.validFrom).getTime();
      const validTo = s.validTo ? new Date(s.validTo).getTime() : null;
      return validFrom <= dateTime && (validTo === null || validTo >= dateTime);
    })
    .sort((a, b) => new Date(b.validFrom || 0).getTime() - new Date(a.validFrom || 0).getTime());

  return historical[0] || matchingDay.find((s) => !s.validFrom) || null;
}

function calculateSollzeit(schedule: WorkSchedule[], weekdayNumber: number, date?: Date): number {
  const selectedSchedule = date
    ? getScheduleForDate(schedule, date, weekdayNumber)
    : schedule.find((s) => weekdayNameToNumber(s.dayOfWeek) === weekdayNumber);

  if (!selectedSchedule) return 0;

  if (selectedSchedule.mode === 'flex') {
    return selectedSchedule.hours || 0;
  }

  if (!selectedSchedule.startTime || !selectedSchedule.endTime) return 0;

  return parseTimeString(selectedSchedule.endTime) - parseTimeString(selectedSchedule.startTime);
}

function calculatePause(entry: TimeEntry): number {
  if (entry.pausedDuration && entry.pausedDuration > 0) {
    return entry.pausedDuration / 60;
  }

  if (entry.type === 'work' && typeof entry.breakMinutes === 'number') {
    return Math.round(entry.breakMinutes);
  }

  return 0;
}

function getEntryDurationHours(entry: TimeEntry): number {
  if (!entry.endTime) return 0;

  const start = new Date(entry.startTime);
  const end = new Date(entry.endTime);
  const pause = entry.type === 'work' ? calculatePause(entry) : 0;

  return Math.max(0, (end.getTime() - start.getTime()) / 3600000 - pause / 60);
}

function toDateKey(date: Date | string): string {
  const value = new Date(date);
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isHoliday(date: Date): boolean {
  const holidays = hd.getHolidays(date.getFullYear());
  return holidays.some(
    (h) =>
      h.type === 'public' &&
      h.date.split(' ')[0] === toDateKey(date)
  );
}

function isHalfWorkingDay(date: Date): boolean {
  const month = date.getMonth();
  const day = date.getDate();
  return month === 11 && (day === 24 || day === 31);
}

function toLocalDateKey(date: Date): string {
  return toDateKey(date);
}

type ExportRow = {
  sortTime: number;
  row: string[];
};

function formatEntryTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatReportDate(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
}

function mapDailyRowsToExportRows(dailyRows: ReportDayRow[]): ExportRow[] {
  return dailyRows
    .map((day) => {
      const workEntries = (day.entries || []).filter((entry) => !entry.type || entry.type === 'work');
      const pause = workEntries.reduce((sum, entry) => sum + calculatePause(entry), 0);
      const startParts = [
        day.isHoliday ? `Feiertag${day.holidayName ? `: ${day.holidayName}` : ''}` : null,
        ...workEntries.map((entry) => formatEntryTime(new Date(entry.startTime))),
      ].filter(Boolean);
      const endParts = workEntries
        .filter((entry) => entry.endTime)
        .map((entry) => formatEntryTime(new Date(entry.endTime!)));

      return {
        sortTime: new Date(`${day.date}T00:00:00`).getTime(),
        row: [
          formatReportDate(day.date),
          startParts.join(', ') || '—',
          endParts.join(', ') || '—',
          `${Math.round(pause)} min`,
          formatTime(day.soll),
          formatTime(day.ist),
          formatTime(day.overtime),
          formatTime(day.undertime),
        ],
      };
    })
    .sort((a, b) => a.sortTime - b.sortTime);
}

function buildExportRows(
  _periodLabel: string,
  entries: TimeEntry[],
  schedule: WorkSchedule[],
  dailyRows?: ReportDayRow[]
): ExportRow[] {
  if (dailyRows && dailyRows.length > 0) {
    return mapDailyRowsToExportRows(dailyRows);
  }

  const result: ExportRow[] = [];

  const entriesByDate = new Map<string, TimeEntry[]>();
  for (const entry of entries) {
    const key = toLocalDateKey(new Date(entry.startTime));
    if (!entriesByDate.has(key)) {
      entriesByDate.set(key, []);
    }
    entriesByDate.get(key)!.push(entry);
  }

  const dateKeys = Array.from(entriesByDate.keys()).sort();

  for (const dateKey of dateKeys) {
    const dayEntries = [...(entriesByDate.get(dateKey) || [])].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    if (dayEntries.length === 0) continue;

    const start = new Date(dayEntries[0].startTime);
    const weekdayNum = getWeekdayNumber(dayEntries[0].startTime);
    const holiday = isHoliday(start);
    const scheduledSoll = calculateSollzeit(schedule, weekdayNum, start);
    const soll = isHalfWorkingDay(start) ? scheduledSoll / 2 : scheduledSoll;

    const workEntries = dayEntries.filter((entry) => !entry.type || entry.type === 'work');
    const sickEntries = dayEntries.filter((entry) => entry.type === 'sick');
    const vacationEntries = dayEntries.filter((entry) => entry.type === 'vacation');
    const pause = workEntries.reduce((sum, entry) => sum + calculatePause(entry), 0);
    const sickHours = sickEntries.reduce((sum, entry) => sum + getEntryDurationHours(entry), 0);
    const vacationHours = vacationEntries.reduce((sum, entry) => sum + getEntryDurationHours(entry), 0);
    const workHours = workEntries.reduce((sum, entry) => sum + getEntryDurationHours(entry), 0);

    const ist = (holiday ? soll : 0) + (holiday ? workHours : workHours + sickHours + vacationHours);
    const ueber = Math.max(0, ist - soll);
    const minus = Math.max(0, soll - ist);
    const startParts = [
      sickEntries.length > 0 ? 'Krank' : null,
      vacationEntries.length > 0 ? 'Urlaub' : null,
      ...workEntries.map((entry) => formatEntryTime(new Date(entry.startTime))),
    ].filter(Boolean);
    const endParts = workEntries
      .filter((entry) => entry.endTime)
      .map((entry) => formatEntryTime(new Date(entry.endTime!)));

    result.push({
      sortTime: start.getTime(),
      row: [
        start.toLocaleDateString(),
        startParts.join(', ') || '—',
        endParts.join(', ') || '—',
        `${Math.round(pause)} min`,
        formatTime(soll),
        formatTime(ist),
        formatTime(ueber),
        formatTime(minus),
      ],
    });
  }

  return result.sort((a, b) => a.sortTime - b.sortTime);
}

async function addLogoToDoc(doc: jsPDF) {
  try {
    const logo = await loadLogoAsBase64();
    const img = new Image();
    img.src = logo;
    await new Promise((resolve) => (img.onload = resolve));

    const maxWidth = 30;
    const scale = maxWidth / img.width;
    const width = maxWidth;
    const height = img.height * scale;

    const pageWidth = doc.internal.pageSize.getWidth();
    const xPos = pageWidth - width - 14;

    doc.addImage(logo, 'PNG', xPos, 10, width, height);
  } catch (err) {
    console.warn('Logo konnte nicht geladen werden:', err);
  }
}

function sanitizeSheetName(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Bericht';
}

function buildMonthlySummaryRows(monthlySummaries: MonthlySummary[] = []) {
  return monthlySummaries.map((summary) => [
    summary.month,
    formatTime(summary.plannedHours),
    formatTime(summary.totalHours),
    formatTime(summary.overtime),
    formatTime(summary.undertime),
  ]);
}

function buildWeeklySummaryRows(weeklySummaries: WeeklySummary[] = []) {
  return weeklySummaries.map((summary) => [
    summary.week,
    formatTime(summary.plannedHours),
    formatTime(summary.totalHours),
    formatTime(summary.overtime),
    formatTime(summary.undertime),
  ]);
}

function addSummaryTableToPDF(
  doc: jsPDF,
  title: string,
  head: string[],
  body: string[][],
  startY: number
) {
  if (body.length === 0) return startY;

  doc.text(title, 14, startY);
  autoTable(doc, {
    startY: startY + 4,
    head: [head],
    body,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [41, 128, 185] },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

const DEFAULT_EXPORT_OPTIONS: ReportExportOptions = {
  monthly: true,
  weekly: true,
  daily: true,
};

export async function exportReportToPDF(
  username: string,
  month: string,
  entries: TimeEntry[],
  summary: Summary,
  schedule: WorkSchedule[],
  options: ReportExportOptions = DEFAULT_EXPORT_OPTIONS
) {
  const doc = new jsPDF();

  await addLogoToDoc(doc);

  doc.setFontSize(14);
  doc.text(`Bericht für ${username}`, 14, 28);
  doc.setFontSize(11);
  doc.text(`Zeitraum: ${month}`, 14, 35);

  const summaryLines = [
    `Sollstunden: ${formatTime(summary.plannedHours)}`,
    `Iststunden: ${formatTime(summary.totalHours)}`,
    `Überstunden: ${formatTime(summary.overtime)}`,
    `Minusstunden: ${formatTime(summary.undertime)}`,
  ];

  summaryLines.forEach((line, i) => {
    doc.text(line, 14, 45 + i * 6);
  });

  let tableStartY = 75;

  if (options.monthly) {
    tableStartY = addSummaryTableToPDF(
      doc,
      'Monatliche Über-/Minusstunden',
      ['Monat', 'Soll', 'Ist', 'Über', 'Minus'],
      buildMonthlySummaryRows(options.monthlySummaries),
      tableStartY
    );
  }

  if (options.weekly) {
    tableStartY = addSummaryTableToPDF(
      doc,
      'Wöchentliche Über-/Minusstunden',
      ['Kalenderwoche', 'Soll', 'Ist', 'Über', 'Minus'],
      buildWeeklySummaryRows(options.weeklySummaries),
      tableStartY
    );
  }

  if (options.daily) {
    const exportRows = buildExportRows(month, entries, schedule, options.dailyRows);
    const tableData = exportRows.map((item) => item.row);

    autoTable(doc, {
      startY: tableStartY,
      head: [['Datum', 'Start', 'Ende', 'Pause', 'Soll', 'Ist', 'Über', 'Minus']],
      body: tableData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
    });
  }

  doc.save(`Bericht_${username}_${month}.pdf`);
}

export function exportReportToExcel(
  username: string,
  month: string,
  entries: TimeEntry[],
  summary: Summary,
  schedule: WorkSchedule[],
  options: ReportExportOptions = DEFAULT_EXPORT_OPTIONS
) {
  const header = [
    ['Name', username],
    ['Zeitraum', month],
    ['Sollstunden', formatTime(summary.plannedHours)],
    ['Iststunden', formatTime(summary.totalHours)],
    ['Überstunden', formatTime(summary.overtime)],
    ['Minusstunden', formatTime(summary.undertime)],
  ];

  if (options.monthly) {
    header.push(
      [],
      ['Monatliche Über-/Minusstunden'],
      ['Monat', 'Sollstunden', 'Iststunden', 'Überstunden', 'Minusstunden'],
      ...buildMonthlySummaryRows(options.monthlySummaries)
    );
  }

  if (options.weekly) {
    header.push(
      [],
      ['Wöchentliche Über-/Minusstunden'],
      ['Kalenderwoche', 'Sollstunden', 'Iststunden', 'Überstunden', 'Minusstunden'],
      ...buildWeeklySummaryRows(options.weeklySummaries)
    );
  }

  if (options.daily) {
    header.push([], ['Datum', 'Start', 'Ende', 'Pause', 'Soll', 'Ist', 'Über', 'Minus']);
  }

  const exportRows = buildExportRows(month, entries, schedule, options.dailyRows);
  const rows = options.daily ? exportRows.map((item) => item.row) : [];

  const worksheet = XLSX.utils.aoa_to_sheet([...header, ...rows]);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Bericht');
  XLSX.writeFile(workbook, `Bericht_${username}_${month}.xlsx`);
}

export async function exportAllReportsToPDF(
  periodLabel: string,
  reports: UserReportExportData[],
  options: ReportExportOptions = DEFAULT_EXPORT_OPTIONS
) {
  const doc = new jsPDF();
  let isFirstUser = true;

  for (const report of reports) {
    if (!isFirstUser) {
      doc.addPage();
    }

    await addLogoToDoc(doc);

    doc.setFontSize(14);
    doc.text(`Bericht für ${report.username}`, 14, 28);
    doc.setFontSize(11);
    doc.text(`Zeitraum: ${periodLabel}`, 14, 35);

    const summaryLines = [
      `Sollstunden: ${formatTime(report.summary.plannedHours)}`,
      `Iststunden: ${formatTime(report.summary.totalHours)}`,
      `Überstunden: ${formatTime(report.summary.overtime)}`,
      `Minusstunden: ${formatTime(report.summary.undertime)}`,
    ];

    summaryLines.forEach((line, i) => {
      doc.text(line, 14, 45 + i * 6);
    });

    let tableStartY = 75;

    if (options.monthly) {
      tableStartY = addSummaryTableToPDF(
        doc,
        'Monatliche Über-/Minusstunden',
        ['Monat', 'Soll', 'Ist', 'Über', 'Minus'],
        buildMonthlySummaryRows(report.monthlySummaries),
        tableStartY
      );
    }

    if (options.weekly) {
      tableStartY = addSummaryTableToPDF(
        doc,
        'Wöchentliche Über-/Minusstunden',
        ['Kalenderwoche', 'Soll', 'Ist', 'Über', 'Minus'],
        buildWeeklySummaryRows(report.weeklySummaries),
        tableStartY
      );
    }

    if (options.daily) {
      const exportRows = buildExportRows(periodLabel, report.entries, report.schedule, report.dailyRows);
      const tableData = exportRows.map((item) => item.row);

      autoTable(doc, {
        startY: tableStartY,
        head: [['Datum', 'Start', 'Ende', 'Pause', 'Soll', 'Ist', 'Über', 'Minus']],
        body: tableData,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185] },
      });
    }

    isFirstUser = false;
  }

  doc.save(`Bericht_Alle_Nutzer_${periodLabel}.pdf`);
}

export function exportAllReportsToExcel(
  periodLabel: string,
  reports: UserReportExportData[],
  options: ReportExportOptions = DEFAULT_EXPORT_OPTIONS
) {
  const workbook = XLSX.utils.book_new();

  for (const report of reports) {
    const header = [
      ['Name', report.username],
      ['Zeitraum', periodLabel],
      ['Sollstunden', formatTime(report.summary.plannedHours)],
      ['Iststunden', formatTime(report.summary.totalHours)],
      ['Überstunden', formatTime(report.summary.overtime)],
      ['Minusstunden', formatTime(report.summary.undertime)],
    ];

    if (options.monthly) {
      header.push(
        [],
        ['Monatliche Über-/Minusstunden'],
        ['Monat', 'Sollstunden', 'Iststunden', 'Überstunden', 'Minusstunden'],
        ...buildMonthlySummaryRows(report.monthlySummaries)
      );
    }

    if (options.weekly) {
      header.push(
        [],
        ['Wöchentliche Über-/Minusstunden'],
        ['Kalenderwoche', 'Sollstunden', 'Iststunden', 'Überstunden', 'Minusstunden'],
        ...buildWeeklySummaryRows(report.weeklySummaries)
      );
    }

    if (options.daily) {
      header.push([], ['Datum', 'Start', 'Ende', 'Pause', 'Soll', 'Ist', 'Über', 'Minus']);
    }

    const exportRows = buildExportRows(periodLabel, report.entries, report.schedule, report.dailyRows);
    const rows = options.daily ? exportRows.map((item) => item.row) : [];

    const worksheet = XLSX.utils.aoa_to_sheet([...header, ...rows]);
    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(report.username));
  }

  XLSX.writeFile(workbook, `Bericht_Alle_Nutzer_${periodLabel}.xlsx`);
}
